import React from 'react';
import { WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ConnectionLostBannerProps {
  status?: 'connecting' | 'connected' | 'disconnected' | 'error';
  error?: Error | null;
  onReconnect?: () => void;
}

export function ConnectionLostBanner({ status = 'disconnected', error, onReconnect }: ConnectionLostBannerProps) {
  // Don't show banner when connected
  if (status === 'connected') {
    return null;
  }

  // Determine styling and content based on status
  const getStatusConfig = () => {
    switch (status) {
      case 'connecting':
        return {
          bgColor: 'bg-amber-500/90',
          icon: <WifiOff className="w-4 h-4" />,
          message: 'Reconnecting...',
          showSpinner: true,
          showButton: false,
        };
      case 'disconnected':
        return {
          bgColor: 'bg-warning-500/90',
          icon: <WifiOff className="w-4 h-4" />,
          message: 'Connection lost',
          showSpinner: false,
          showButton: true,
        };
      case 'error':
        return {
          bgColor: 'bg-red-500/90',
          icon: <AlertCircle className="w-4 h-4" />,
          message: error?.message || 'Connection error occurred',
          showSpinner: false,
          showButton: true,
        };
      default:
        return {
          bgColor: 'bg-warning-500/90',
          icon: <WifiOff className="w-4 h-4" />,
          message: 'Connection issue',
          showSpinner: false,
          showButton: true,
        };
    }
  };

  const config = getStatusConfig();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 ${config.bgColor} backdrop-blur text-white px-4 py-2 flex items-center justify-center gap-4 z-50 shadow-lg`}
      >
        <div className="flex items-center gap-2">
          {config.icon}
          <span className="font-medium text-sm">
            {config.message}
          </span>
        </div>

        {config.showSpinner && (
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        )}

        {config.showButton && onReconnect && (
          <button
            onClick={onReconnect}
            className="flex items-center gap-1.5 text-xs bg-black/20 hover:bg-black/30 px-3 py-1 rounded transition-colors font-medium"
          >
            <RefreshCw className="w-3 h-3" />
            Reconnect
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
}